/**
 * Booking category (tab) definitions
 *
 * The bookings overview groups bookings into tabs. This module is the single
 * source of truth for that mapping so the server-side filter and any remaining
 * client-side categorisation cannot drift apart.
 *
 * The mapping depends ONLY on `booking_state`. The previous client-side
 * implementation also compared `start_date`/`end_date` against the current
 * time, but those comparisons never changed the outcome:
 *
 *   cancelled                        -> 'cancelled'
 *   returned                         -> 'returned'
 *   active, dates spanning now       -> 'active'   (date branch)
 *   active, any other dates          -> 'active'   (final ternary)
 *   open, start in the future        -> 'upcoming' (date branch)
 *   open, any other dates            -> 'upcoming' (final ternary)
 *   anything else (no_show, ...)     -> 'upcoming' (final ternary)
 *
 * So every state except cancelled/returned/active lands in 'upcoming'. That is
 * why the 'upcoming' filter below is expressed as NOT IN rather than
 * `= 'open'`: states such as `no_show` and `confirmed` exist elsewhere in the
 * codebase and must keep appearing under 'upcoming' instead of vanishing from
 * the UI entirely.
 */

export type BookingCategory = 'upcoming' | 'active' | 'returned' | 'cancelled' | 'all';

/** Categories that map 1:1 onto a single `booking_state` value. */
const STATE_BACKED_CATEGORIES = {
  active: 'active',
  returned: 'returned',
  cancelled: 'cancelled',
} as const;

/**
 * States that belong to a tab other than 'upcoming'. Everything NOT in this
 * list is 'upcoming'.
 */
const NON_UPCOMING_STATES = Object.values(STATE_BACKED_CATEGORIES);

/**
 * Which tab a booking appears under, derived from its state alone.
 */
export function categoryFromBookingState(state: string): Exclude<BookingCategory, 'all'> {
  for (const [category, matchingState] of Object.entries(STATE_BACKED_CATEGORIES)) {
    if (state === matchingState) return category as Exclude<BookingCategory, 'all'>;
  }
  return 'upcoming';
}

/**
 * Constrain a Supabase query to a single category.
 *
 * This is what keeps the overview's default view small: the 'upcoming' tab
 * fetches only live bookings instead of the merchant's entire history, which
 * for a busy merchant is the difference between tens of rows and thousands.
 *
 * `all` (or no category) applies no constraint.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyBookingCategoryFilter(query: any, category?: BookingCategory): any {
  if (!category || category === 'all') return query;

  const state = STATE_BACKED_CATEGORIES[category as keyof typeof STATE_BACKED_CATEGORIES];
  if (state) return query.eq('booking_state', state);

  // 'upcoming' = everything that is not one of the other tabs.
  const quoted = NON_UPCOMING_STATES.map((s) => `"${s}"`).join(',');
  return query.not('booking_state', 'in', `(${quoted})`);
}
